<app-report-header
  crumbLabel="Investment Property Calculator"
  crumbSuffix="Investment Report"
  [loadingDownload]="loadingDownloadbtn"
  (download)="convetToPDF()"
  (recalculate)="startagain()">
</app-report-header>

<!-- RESULTS PAGE — white professional report design matching Adventum Report reference -->
<div class="rpt-page" id="contentToConvert">

  <!-- ── Masthead ── -->
  <div class="rpt-wrap rpt-masthead">
    <div class="rpt-masthead__eyebrow">Investment Property Report</div>
    <h1 class="rpt-masthead__title">{{calcData?.City || 'UK'}}{{calcData?.Pincode ? ' (' + calcData?.Pincode + ')' : ''}} — Buy-to-Let Investment Analysis</h1>
    <p class="rpt-masthead__subtitle" *ngIf="calcData?.ForeignBuyer === '1'">
      Three-dimensional return projection on a {{calcData?.PropertyValue | currency:'GBP':'symbol':'1.0-0'}} property over a {{calcData?.investedTenure}}-year holding period, modelled in your home currency with full FX appreciation captured.
    </p>
    <p class="rpt-masthead__subtitle" *ngIf="calcData?.ForeignBuyer !== '1'">
      Two-dimensional return projection on a {{calcData?.PropertyValue | currency:'GBP':'symbol':'1.0-0'}} property over a {{calcData?.investedTenure}}-year holding period — rental income and capital growth in GBP.
    </p>
    <div class="rpt-masthead__meta">
      <span class="rpt-meta-pill">Property Value <strong>{{calcData?.PropertyValue | currency:'GBP':'symbol':'1.0-0'}}</strong></span>
      <span class="rpt-meta-pill">Period <strong>{{calcData?.investedTenure}} years</strong></span>
      <span class="rpt-meta-pill" *ngIf="calcData?.ForeignBuyer=='1'">Buyer Status <strong>Foreign Buyer</strong></span>
      <span class="rpt-meta-pill rpt-meta-pill--accent" *ngIf="calcData?.ForeignBuyer === '1'">Home Currency <strong>{{homecurrencyText}} · {{calcData?.homecurrency}} / £</strong></span>
    </div>
  </div>

  <!-- ── §01 Headline Returns ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">01 · Results</div>
        <h2 class="rpt-section__title">Headline returns</h2>
        <p class="rpt-section__sub" *ngIf="calcData?.ForeignBuyer === '1'">
          The home-currency IRR captures every driver of return — including the FX tailwind that domestic asset classes don't offer.
        </p>
        <p class="rpt-section__sub" *ngIf="calcData?.ForeignBuyer !== '1'">
          Your annualised GBP return, combining rental income and capital growth over the holding period.
        </p>
      </div>
      <span class="rpt-section__tag" *ngIf="calcData?.ForeignBuyer === '1'">{{calcData?.investedTenure}}-year horizon · {{homecurrencyText}} · {{calcData?.homecurrency}} / £</span>
      <span class="rpt-section__tag" *ngIf="calcData?.ForeignBuyer !== '1'">{{calcData?.investedTenure}}-year horizon · GBP</span>
    </div>

    <div class="rpt-kpi-strip">
      <!-- Hero: IRR -->
      <div class="rpt-kpi-hero">
        <div class="rpt-kpi-hero__eyebrow">
          {{ calcData?.ForeignBuyer === '1' ? 'Internal Rate of Return · Home Currency' : 'Internal Rate of Return · GBP' }}
        </div>
        <div class="rpt-kpi-hero__body">
          <div style="display:flex; align-items:baseline; gap:16px; flex-wrap:wrap;">
            <div class="rpt-kpi-hero__value">{{IrrInHomeCurrency | number:'1.2-2'}}<span class="rpt-kpi-hero__unit">%</span></div>
            <span class="rpt-kpi-hero__currency">in {{homecurrencyText}}</span>
          </div>
          <div class="rpt-kpi-hero__label" *ngIf="calcData?.ForeignBuyer === '1'">Annualised, including FX appreciation</div>
          <div class="rpt-kpi-hero__label" *ngIf="calcData?.ForeignBuyer !== '1'">Annualised GBP return</div>
        </div>
        <p class="rpt-kpi-hero__caption" *ngIf="calcData?.ForeignBuyer === '1'">
          The compound rate at which your initial {{formatCurrency(-graphInitialInvestmentInHomeCurrency, homecurrencyText)}} investment grows over {{calcData?.investedTenure}} years, factoring rental income, capital growth, and GBP appreciation.
        </p>
        <p class="rpt-kpi-hero__caption" *ngIf="calcData?.ForeignBuyer !== '1'">
          The compound rate at which your initial {{formatCurrency(-graphInitialInvestmentInHomeCurrency, 'GBP')}} investment grows over {{calcData?.investedTenure}} years, factoring rental income and capital growth.
        </p>
      </div>

      <!-- Side: Wealth Multiple, Total Profit, (Home Currency IRR for foreign buyers only) -->
      <div class="rpt-kpi-side">
        <div class="rpt-kpi-card">
          <div class="rpt-kpi-card__label">Wealth Multiple</div>
          <div class="rpt-kpi-card__value">{{wealthMultiple}}<sup>×</sup></div>
          <div class="rpt-kpi-card__foot">
            <span class="rpt-accent">ROE {{returnOnEquity | number:'1.0-0'}}%</span>
          </div>
        </div>
        <div class="rpt-kpi-card">
          <div class="rpt-kpi-card__label">Total Profit</div>
          <div class="rpt-kpi-card__value" *ngIf="calcData?.ForeignBuyer === '1'">{{formatCurrency(TotalReturnOfInvestment, homecurrencyText)}}</div>
          <div class="rpt-kpi-card__value" *ngIf="calcData?.ForeignBuyer !== '1'">{{formatCurrency(TotalReturnInGBP, 'GBP')}}</div>
          <div class="rpt-kpi-card__foot">Net of all costs and SDLT</div>
        </div>
        <div class="rpt-kpi-card" *ngIf="calcData?.ForeignBuyer === '1'">
          <div class="rpt-kpi-card__label">IRR (GBP)</div>
          <div class="rpt-kpi-card__value">{{IrrInGBP | number:'1.2-2'}}<sup>%</sup></div>
          <div class="rpt-kpi-card__foot">GBP return</div>
        </div>
        <div class="rpt-kpi-card" *ngIf="calcData?.ForeignBuyer !== '1'">
          <div class="rpt-kpi-card__label">Net Rental Income</div>
          <div class="rpt-kpi-card__value">{{formatCurrency(RentalIncomeGBP, 'GBP')}}</div>
          <div class="rpt-kpi-card__foot">Over {{calcData?.investedTenure}} years</div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §02 Returns Composition ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">02 · Composition</div>
        <h2 class="rpt-section__title">Where your returns come from</h2>
        <p class="rpt-section__sub" *ngIf="calcData?.ForeignBuyer === '1'">Breakdown of total profit by source — in both home currency and sterling.</p>
        <p class="rpt-section__sub" *ngIf="calcData?.ForeignBuyer !== '1'">Breakdown of total GBP profit by source — rental income and capital growth.</p>
      </div>
    </div>
    <div class="irr-donut-pair">
      <!-- Home Currency donut — foreign buyers only -->
      <div class="irr-donut-card" [hidden]="calcData?.ForeignBuyer !== '1'">
        <div class="irr-donut-card__head">
          <div class="irr-donut-card__title">Total Profit · {{homecurrencyText}}</div>
          <div class="irr-donut-card__total">{{formatCurrency(TotalReturnOfInvestment, homecurrencyText)}}</div>
          <div class="irr-donut-card__period">over {{calcData?.investedTenure}} years</div>
        </div>
        <div class="irr-donut-card__canvas-wrap">
          <canvas id="can"></canvas>
        </div>
        <div class="irr-donut-card__legend">
          <div class="irr-legend-row">
            <div class="irr-legend-row__left">
              <span class="irr-legend-swatch" style="background:#132147"></span>
              <span>Capital Growth</span>
            </div>
            <span class="irr-legend-pct">{{TotalReturnOfInvestment > 0 ? (capitalAppreciation / TotalReturnOfInvestment * 100).toFixed(1) : '0.0'}}%</span>
          </div>
          <div class="irr-legend-row">
            <div class="irr-legend-row__left">
              <span class="irr-legend-swatch" style="background:#4a5a85"></span>
              <span>FX Appreciation</span>
            </div>
            <span class="irr-legend-pct">{{TotalReturnOfInvestment > 0 ? (fxAppreciation / TotalReturnOfInvestment * 100).toFixed(1) : '0.0'}}%</span>
          </div>
          <div class="irr-legend-row">
            <div class="irr-legend-row__left">
              <span class="irr-legend-swatch" style="background:#8a99b8"></span>
              <span>Net Rental Income</span>
            </div>
            <span class="irr-legend-pct">{{TotalReturnOfInvestment > 0 ? (RentalIncome / TotalReturnOfInvestment * 100).toFixed(1) : '0.0'}}%</span>
          </div>
        </div>
      </div>

      <!-- GBP donut — full width for UK residents -->
      <div class="irr-donut-card" [class.irr-donut-card--solo]="calcData?.ForeignBuyer !== '1'">
        <div class="irr-donut-card__head">
          <div class="irr-donut-card__title">Total Profit · GBP</div>
          <div class="irr-donut-card__total">{{formatCurrency(TotalReturnInGBP, 'GBP')}}</div>
          <div class="irr-donut-card__period">over {{calcData?.investedTenure}} years</div>
        </div>
        <div class="irr-donut-card__canvas-wrap">
          <canvas id="can-gbp"></canvas>
        </div>
        <div class="irr-donut-card__legend">
          <div class="irr-legend-row">
            <div class="irr-legend-row__left">
              <span class="irr-legend-swatch" style="background:#132147"></span>
              <span>Capital Growth</span>
            </div>
            <span class="irr-legend-pct">{{TotalReturnInGBP > 0 ? (capitalAppreciationGBP / TotalReturnInGBP * 100).toFixed(1) : '0.0'}}%</span>
          </div>
          <div class="irr-legend-row">
            <div class="irr-legend-row__left">
              <span class="irr-legend-swatch" style="background:#8a99b8"></span>
              <span>Net Rental Income</span>
            </div>
            <span class="irr-legend-pct">{{TotalReturnInGBP > 0 ? (RentalIncomeGBP / TotalReturnInGBP * 100).toFixed(1) : '0.0'}}%</span>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §03 Investment Journey ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">03 · Journey</div>
        <h2 class="rpt-section__title">Your investment journey</h2>
        <p class="rpt-section__sub">Total profit breakdown over the {{calcData?.investedTenure}}-year holding period — from initial outlay to value at exit.</p>
      </div>
    </div>
    <div class="irr-card irr-card--journey">
      <div class="irr-journey-head">
        <div class="irr-journey-title">From {{formatCurrency(getJourneyInitialInvestment(), homecurrencyText)}} to {{formatCurrency(getJourneyInitialInvestment() + getJourneyTotalProfit(), homecurrencyText)}}</div>
        <div class="irr-journey-sub" *ngIf="calcData?.ForeignBuyer === '1'">Each bar shows the cumulative {{homecurrencyText}} value at each step</div>
        <div class="irr-journey-sub" *ngIf="calcData?.ForeignBuyer !== '1'">Each bar shows the cumulative GBP value at each step</div>
      </div>
      <div class="irr-journey-chart-wrap">
        <canvas id="journeyCanvas"></canvas>
      </div>
      <div class="irr-formula-chips">
        <div class="irr-formula-chip">
          <span class="irr-formula-chip__lbl">INITIAL</span>
          <span class="irr-formula-chip__val">{{formatCurrency(getJourneyInitialInvestment(), homecurrencyText)}}</span>
        </div>
        <span class="irr-formula-op">+</span>
        <div class="irr-formula-chip">
          <span class="irr-formula-chip__lbl">NET RENT</span>
          <span class="irr-formula-chip__val">{{formatCurrency(getJourneyNetRental(), homecurrencyText)}}</span>
        </div>
        <span class="irr-formula-op">+</span>
        <div class="irr-formula-chip">
          <span class="irr-formula-chip__lbl">CAP GROWTH</span>
          <span class="irr-formula-chip__val">{{formatCurrency(getJourneyCapitalGrowth(), homecurrencyText)}}</span>
        </div>
        <ng-container *ngIf="calcData?.ForeignBuyer === '1'">
          <span class="irr-formula-op">+</span>
          <div class="irr-formula-chip">
            <span class="irr-formula-chip__lbl">FX</span>
            <span class="irr-formula-chip__val">{{formatCurrency(getJourneyFxAppreciation(), homecurrencyText)}}</span>
          </div>
        </ng-container>
        <span class="irr-formula-op">=</span>
        <div class="irr-formula-chip irr-formula-chip--exit">
          <span class="irr-formula-chip__lbl">EXIT VALUE</span>
          <span class="irr-formula-chip__val">{{formatCurrency(getJourneyInitialInvestment() + getJourneyTotalProfit(), homecurrencyText)}}</span>
        </div>
      </div>
    </div>
  </section>

  <!-- ── §04 Benchmarks ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">04 · Benchmark</div>
        <h2 class="rpt-section__title">How does this investment compare?</h2>
        <p class="rpt-section__sub" *ngIf="homecurrencyText === 'INR'">Your {{homecurrencyText}} IRR placed alongside common Indian investment alternatives over a comparable {{calcData?.investedTenure}}-year horizon.</p>
        <p class="rpt-section__sub" *ngIf="homecurrencyText !== 'INR'">Your {{homecurrencyText}} IRR placed alongside common investment alternatives over a comparable {{calcData?.investedTenure}}-year horizon.</p>
      </div>
      <span class="rpt-section__tag">Annualised returns · {{calcData?.investedTenure}}-year horizon</span>
    </div>
    <div class="irr-card irr-card--bench">
      <div class="bench-chart-wrap">
        <canvas id="benchCanvas"></canvas>
      </div>
      <ng-container *ngIf="calcData?.ForeignBuyer === '1'">
        <div class="bench-note-card" *ngIf="homecurrencyText === 'INR'">
          At <strong>{{IrrInHomeCurrency | number:'1.2-2'}}% IRR</strong>, this property outperforms major asset classes over a comparable {{calcData?.investedTenure}}-year horizon. The FX tailwind adds approximately <strong>{{calcData?.fxgrowth}} percentage points</strong> per annum that domestic Indian investments fail to capture.
        </div>
        <div class="bench-note-card" *ngIf="homecurrencyText !== 'INR'">
          At <strong>{{IrrInHomeCurrency | number:'1.2-2'}}% IRR ({{homecurrencyText}})</strong>, this property outperforms major UK asset classes over a comparable {{calcData?.investedTenure}}-year horizon. The FX tailwind provides an additional return that domestic {{homecurrencyText}} investments don't capture.
        </div>
      </ng-container>
      <div class="bench-note-card" *ngIf="calcData?.ForeignBuyer !== '1'">
        At <strong>{{IrrInGBP | number:'1.2-2'}}% GBP IRR</strong>, this property outperforms major UK asset classes over a comparable {{calcData?.investedTenure}}-year horizon.
      </div>
    </div>
  </section>

  <!-- ── §05 IRR Trajectory ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">05 · Trajectory</div>
        <h2 class="rpt-section__title">IRR trajectory over holding period</h2>
        <p class="rpt-section__sub">Annualised internal rate of return, measured at each year-end.</p>
      </div>
    </div>
    <div class="irr-card">
      <div class="rp-card-header">
        <h3>IRR Trajectory</h3>
        <p>Annualised IRR at each year-end</p>
      </div>
      <div class="rp-chart-wrap">
        <canvas id="canvas" class="chartjs-render-monitor printimportant"></canvas>
      </div>
      <div class="rp-chart-legend">
        <span><i style="background:#4a5a85;display:inline-block;height:16px;width:16px;border-radius:4px;vertical-align:middle"></i> GBP IRR</span>
        <span *ngIf="calcData?.ForeignBuyer === '1'"><i style="background:#23B14B;display:inline-block;height:16px;width:16px;border-radius:4px;vertical-align:middle"></i> {{homecurrencyText}} IRR</span>
      </div>
      <div class="rp-irr-note-card" *ngIf="calcData?.ForeignBuyer === '1'">
        Early years show lower IRR because upfront costs (SDLT, fees) drag the annualised return. By Year 3, compounding rental income and capital growth overcome the initial drag. The gap between GBP and {{homecurrencyText}} widens as FX appreciation compounds.
      </div>
      <div class="rp-irr-note-card" *ngIf="calcData?.ForeignBuyer !== '1'">
        Early years show lower IRR because upfront costs (SDLT, fees) drag the annualised return. By Year 3, compounding rental income and capital growth overcome the initial drag.
      </div>
    </div>
  </section>

  <!-- ── §06 Your Inputs ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">06 · Assumptions</div>
        <h2 class="rpt-section__title">Your inputs</h2>
        <p class="rpt-section__sub">All values used to generate this calculation.</p>
      </div>
    </div>
    <div class="rpt-inputs">
      <div class="rpt-inputs__grid">
        <!-- Header row: Property Location -->
        <div class="rpt-inputs__row rpt-inputs__row--full">
          <span class="rpt-inputs__key" style="font-weight:600; color:var(--navy)">Property Location</span>
          <span class="rpt-inputs__val">{{calcData?.City || '—'}}{{calcData?.Pincode ? ' (' + calcData?.Pincode + ')' : ''}}</span>
        </div>
        <!-- Row 1 -->
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Property Value</span>
          <span class="rpt-inputs__val">{{calcData?.PropertyValue | currency:'GBP':'symbol':'1.0-0'}}</span>
        </div>
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Loan Amount</span>
          <span class="rpt-inputs__val" *ngIf="calcData?.optmortgage=='1'">{{calcData?.PropertyValue * calcData?.loanAmount / 100 | currency:'GBP':'symbol':'1.0-0'}}</span>
          <span class="rpt-inputs__val" *ngIf="calcData?.optmortgage!='1'">No Mortgage</span>
        </div>
        <!-- Row 2 -->
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Mortgage Interest Rate</span>
          <span class="rpt-inputs__val" *ngIf="calcData?.optmortgage=='1'">{{calcData?.mortgageInterestRate}}%</span>
          <span class="rpt-inputs__val" *ngIf="calcData?.optmortgage!='1'">—</span>
        </div>
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Mortgage Type</span>
          <span class="rpt-inputs__val" *ngIf="calcData?.optmortgage=='1'">{{calcData?.mortgageType=='1' ? 'Interest Only' : 'Principal + Interest'}}</span>
          <span class="rpt-inputs__val" *ngIf="calcData?.optmortgage!='1'">—</span>
        </div>
        <!-- Row 3 -->
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Mortgage Tenure</span>
          <span class="rpt-inputs__val" *ngIf="calcData?.optmortgage=='1'">{{calcData?.mortgageTenure}} years</span>
          <span class="rpt-inputs__val" *ngIf="calcData?.optmortgage!='1'">—</span>
        </div>
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Investment Tenure</span>
          <span class="rpt-inputs__val">{{calcData?.investedTenure}} years</span>
        </div>
        <!-- Row 4 -->
        <div class="rpt-inputs__row" *ngIf="calcData?.ForeignBuyer === '1'">
          <span class="rpt-inputs__key">Home Currency (1 GBP)</span>
          <span class="rpt-inputs__val">{{homecurrencyText}} {{calcData?.homecurrency}}</span>
        </div>
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Rental Yield</span>
          <span class="rpt-inputs__val">{{calcData?.rentalYeild}}%</span>
        </div>
        <!-- Row 5 -->
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Rental Escalation</span>
          <span class="rpt-inputs__val">{{calcData?.rentalGrowthEscalation}}%</span>
        </div>
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Capital Growth</span>
          <span class="rpt-inputs__val">{{calcData?.capitalgrowth}}%</span>
        </div>
        <!-- Row 6 -->
        <div class="rpt-inputs__row" *ngIf="calcData?.ForeignBuyer === '1'">
          <span class="rpt-inputs__key">FX Appreciation</span>
          <span class="rpt-inputs__val">{{calcData?.fxgrowth}}%</span>
        </div>
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Stamp Duty (SDLT)</span>
          <span class="rpt-inputs__val">{{calcData?.stampDutyLandTax | currency:'GBP':'symbol':'1.0-0'}}</span>
        </div>
        <!-- Row 7 -->
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Loan Origination Fee</span>
          <span class="rpt-inputs__val">{{calcData?.loanOriginationFee || 0}}%</span>
        </div>
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Letting Management</span>
          <span class="rpt-inputs__val">{{calcData?.letteingManagFee}}%</span>
        </div>
        <!-- Row 8 -->
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Legal Fees</span>
          <span class="rpt-inputs__val">{{(calcData?.legalFees || 0) | currency:'GBP':'symbol':'1.0-0'}}</span>
        </div>
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Service Charges</span>
          <span class="rpt-inputs__val">{{(calcData?.serviceCharges || 0) | currency:'GBP':'symbol':'1.0-0'}}</span>
        </div>
        <!-- Row 9 -->
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Misc. Expenses</span>
          <span class="rpt-inputs__val">{{(calcData?.miscelleneousExpense || 0) | currency:'GBP':'symbol':'1.0-0'}}</span>
        </div>
        <div class="rpt-inputs__row">
          <span class="rpt-inputs__key">Foreign Buyer</span>
          <span class="rpt-inputs__val">{{calcData?.ForeignBuyer=='1' ? 'Yes' : 'No'}}</span>
        </div>
      </div>
    </div>
  </section>

  <!-- ── Annexure A divider ── -->
  <div class="rpt-wrap irr-annexure-divider">
    <div class="irr-annexure-pill">Annexure A</div>
  </div>

  <!-- ── Annexure masthead ── -->
  <section class="rpt-wrap rpt-section">
    <div class="ann-masthead">
      <div class="ann-masthead__head">
        <div>
          <div class="ann-masthead__eyebrow">Local Market Intelligence</div>
          <h2 class="ann-masthead__title">{{calcData?.City || 'Manchester'}} ({{calcData?.Pincode || 'M1'}})</h2>
          <div class="ann-masthead__meta">
            <span>Prepared for Adventum Investors</span>
            <span style="color:rgba(255,255,255,0.3)">·</span>
            <span>{{validation.getCurrentDateWithSpecificFormat()}}</span>
          </div>
        </div>
        <div class="ann-masthead__badge">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
          Local Market Intelligence Report
        </div>
      </div>
    </div>
  </section>

  <!-- ── A.01 Executive Summary ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">A.01 · Executive Summary</div>
        <h2 class="rpt-section__title">Key market indicators at a glance</h2>
        <p class="rpt-section__sub">Headline indicators that most directly bear on buy-to-let return projections.</p>
      </div>
    </div>
    <div class="ann-exec-stats">
      <ng-container *ngFor="let stat of annexureData.executiveStats">
        <div class="ann-exec-stat" *ngIf="(!stat.label.includes('INR') && !stat.label.includes('USD')) || (stat.label.includes('INR') && homecurrencyText === 'INR') || (stat.label.includes('USD') && homecurrencyText !== 'INR' && homecurrencyText !== 'GBP')">
          <div class="ann-exec-stat__value" [class.ann-exec-stat__value--accent]="stat.tone === 'green'">{{stat.value}}</div>
          <div class="ann-exec-stat__label">{{stat.label}}</div>
        </div>
      </ng-container>
    </div>
    <div class="ann-prose">
      <p *ngFor="let paragraph of annexureData.executiveParagraphs">{{paragraph}}</p>
    </div>
  </section>

  <!-- ── A.02 + A.03 Rate & Mortgage ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">A.02 + A.03 · Rate &amp; Mortgage Market</div>
        <h2 class="rpt-section__title">Macro backdrop driving local economics</h2>
        <p class="rpt-section__sub">The Bank of England's policy stance and the UK BTL mortgage market — the two macro inputs that most directly shape buy-to-let underwriting.</p>
      </div>
    </div>
    <div class="ann-data-tables">
      <div class="ann-data-table">
        <div class="ann-data-table__head">
          <div class="ann-data-table__num">A.02</div>
          <div class="ann-data-table__title">Bank of England Base Rate Analysis</div>
          <div class="ann-data-table__sub">MPC trajectory — current stance and forward guidance</div>
        </div>
        <div class="ann-data-table__columns"><span>Indicator</span><span>Value</span></div>
        <div class="ann-data-table__body">
          <div class="ann-data-table__row" *ngFor="let row of annexureData.boeRows">
            <span class="ann-data-table__key">{{row.label}}</span>
            <span class="ann-data-table__val" [class.ann-data-table__val--accent]="row.highlight">{{row.value}}</span>
          </div>
        </div>
        <div class="ann-data-table__foot">{{annexureData.boeNote}}</div>
      </div>
      <div class="ann-data-table">
        <div class="ann-data-table__head">
          <div class="ann-data-table__num">A.03</div>
          <div class="ann-data-table__title">UK Mortgage Market &amp; BTL Costs</div>
          <div class="ann-data-table__sub">Current buy-to-let rates and market activity</div>
        </div>
        <div class="ann-data-table__columns"><span>Mortgage Indicator</span><span>Rate / Value</span></div>
        <div class="ann-data-table__body">
          <div class="ann-data-table__row" *ngFor="let row of annexureData.mortgageRows">
            <span class="ann-data-table__key">{{row.label}}</span>
            <span class="ann-data-table__val" [class.ann-data-table__val--accent]="row.highlight">{{row.value}}</span>
          </div>
        </div>
        <div class="ann-data-table__foot">{{annexureData.mortgageNote}}</div>
      </div>
    </div>
  </section>

  <!-- ── A.04 Local Market Outlook ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">A.04 · Local Market Outlook</div>
        <h2 class="rpt-section__title">{{annexureData.outlookTitle}}</h2>
        <p class="rpt-section__sub">{{annexureData.outlookSub}}</p>
      </div>
    </div>
    <div class="ann-outlook-grid">
      <div class="ann-outlook-card" *ngFor="let card of annexureData.outlookCards">
        <div class="ann-outlook-card__title">{{card.title}}</div>
        <div class="ann-outlook-card__body" [innerHTML]="card.bodyHtml"></div>
      </div>
    </div>
  </section>

  <!-- ── A.05 Financial News ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">A.05 · Financial News</div>
        <h2 class="rpt-section__title">Macro &amp; structural drivers</h2>
        <p class="rpt-section__sub">{{annexureData.financialNewsSub}}</p>
      </div>
    </div>
    <div class="ann-news-grid">
      <ng-container *ngFor="let card of annexureData.financialNewsLeft; let i = index">
        <div class="ann-news-card">
          <div class="ann-news-card__title">{{card.title}}</div>
          <div class="ann-news-card__body">{{card.body}}</div>
        </div>
        <div class="ann-news-card" *ngIf="annexureData.financialNewsRight[i]">
          <div class="ann-news-card__title">{{annexureData.financialNewsRight[i].title}}</div>
          <div class="ann-news-card__body">{{annexureData.financialNewsRight[i].body}}</div>
        </div>
      </ng-container>
    </div>
  </section>

  <!-- ── A.06 Regional Comparables ── -->
  <section class="rpt-wrap rpt-section">
    <div class="rpt-section__head">
      <div>
        <div class="rpt-section__num">A.06 · Regional Comparables</div>
        <h2 class="rpt-section__title">UK city comparison for buy-to-let</h2>
        <p class="rpt-section__sub">{{annexureData.comparablesSub}}</p>
      </div>
    </div>
    <div class="ann-comparables">
      <div class="ann-comparables__head">
        <span>City</span>
        <span>Avg. Yield</span>
        <span>2026 Growth</span>
        <span>Avg. Price</span>
        <span>BTL Rating</span>
      </div>
      <div class="ann-comparables__row" *ngFor="let row of annexureData.comparables"
        [class.ann-comparables__row--hero]="row.highlight || isComparableSelected(row.city)">
        <span class="ann-comparables__city">{{row.city}}</span>
        <span class="ann-comparables__cell" [class.ann-comparables__cell--accent]="row.highlight || isComparableSelected(row.city)">{{row.yield}}</span>
        <span class="ann-comparables__cell">{{row.growth}}</span>
        <span class="ann-comparables__cell">{{row.price}}</span>
        <span class="ann-comparables__cell">
          <span class="ann-rating" [class.ann-rating--strong]="row.ratingTone === 'green'" [class.ann-rating--dim]="row.ratingTone !== 'green'">{{row.rating}}</span>
        </span>
      </div>
    </div>
    <div class="ann-disclaimer">
      <strong>Disclaimer</strong> — This annexure has been prepared by Adventum for informational purposes only and does not constitute investment advice, a personal recommendation, or an offer or solicitation to buy or sell any property or financial instrument. Past performance is not indicative of future results. Property values and rental income may go down as well as up. Data sourced from Bank of England, UK Finance, ONS, JLL, Savills, CBRE, Avison Young, and Moneyfacts as of February 2026.
    </div>
  </section>

</div><!-- /rpt-page -->

<app-report-footer
  [today]="today"
  [loadingDownload]="loadingDownloadbtn"
  [loadingShare]="loadingSharebtn"
  (download)="convetToPDF()"
  (recalculate)="startagain()"
  (share)="shareReport($event)">
</app-report-footer>
